import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNav } from "../../context/NavContext";
import {
  fetchFlashPnlAvailableDates,
  fetchHomeSummary,
} from "../../services/Axios";
import {
  calculateBasisPoints,
  calculateNavPercentage,
  formatBasisPoints,
  formatPercentage,
} from "../../utils/dataCalculations";
import {
  GridContainer,
  MetricCard,
  PageHeader,
  Section,
  SummaryBox,
} from "../ui";

const SEGMENT_TITLES = {
  firmwide: "Firmwide Summary",
  mab: "Merger Arbitrage",
  credit: "Event-Driven Credit",
  spac: "SPAC",
  event: "Convertible Arbitrage",
  creditArb: "Credit Arbitrage",
  rest: "Hedges & Other",
};

/** Archive runs in the morning; if archive_date is YYYY-MM-DD, PnL is as of the previous day. */
function pnlAsOfDate(archiveDateStr) {
  if (!archiveDateStr) return null;
  const d = new Date(archiveDateStr + "T12:00:00");
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

const emptySummary = () => ({
  firmwide: {
    totalPnl: 0,
    totalLmv: 0,
    totalSmv: 0,
    buyTradeMV: 0,
    sellTradeMV: 0,
    shortTradeMV: 0,
    coverTradeMV: 0,
    winners: [],
    losers: [],
  },
  mab: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
  credit: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
  spac: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
  event: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
  creditArb: {
    totalPnl: 0,
    totalLmv: 0,
    totalSmv: 0,
    winners: [],
    losers: [],
  },
  rest: { totalPnl: 0, totalLmv: 0, totalSmv: 0, winners: [], losers: [] },
});

const FlashPnl = () => {
  const theme = useTheme();
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [summary, setSummary] = useState(emptySummary());
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState(null);
  // Live NAV is the fallback; fetchHomeSummary can return an as-of-archive-date
  // NAV below, which overrides it for the selected date.
  const liveNav = useNav();
  const [nav, setNav] = useState(liveNav);

  const loadAvailableDates = useCallback(async () => {
    setLoadingDates(true);
    setError(null);
    try {
      const res = await fetchFlashPnlAvailableDates();
      const dates = Array.isArray(res?.dates) ? res.dates : [];
      setAvailableDates(dates);
      setSelectedDate((prev) =>
        prev ? prev : dates.length > 0 ? dates[0] : null,
      );
    } catch (e) {
      console.error(e);
      setError("Could not load available dates.");
    } finally {
      setLoadingDates(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableDates();
  }, [loadAvailableDates]);

  useEffect(() => {
    if (!selectedDate) {
      setSummary(emptySummary());
      return;
    }
    let cancelled = false;
    setLoadingSummary(true);
    setError(null);
    fetchHomeSummary({ date: selectedDate })
      .then((data) => {
        if (cancelled) return;
        if (data?.summary) {
          setSummary(data.summary);
          if (data.nav != null) setNav(Number(data.nav) || liveNav);
        } else {
          setSummary(emptySummary());
          setError("No summary data for this date.");
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setSummary(emptySummary());
          setError("No archive data for this date.");
        } else {
          setError("Failed to load flash PnL.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSummary(false);
      });
    return () => {
      cancelled = true;
    };
    // liveNav is only read as a fallback inside the .then() above; it must
    // not re-trigger this fetch when the live NAV ticks — only date changes
    // should refetch the archived summary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const firmwide = summary?.firmwide ?? emptySummary().firmwide;

  return (
    <Box sx={{ width: "100%" }}>
      <Section>
        <PageHeader title="Archived Flash PnL" />

        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Select date:
          </Typography>
          <Autocomplete
            value={selectedDate}
            onChange={(_, value) => setSelectedDate(value)}
            options={availableDates}
            loading={loadingDates}
            getOptionLabel={(opt) => opt || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Archive date"
                placeholder="Select date"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingDates ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ minWidth: 200 }}
            disableClearable={availableDates.length === 0}
          />
          {selectedDate && (
            <Typography variant="body2" color="text.secondary">
              Archive date {selectedDate}
              {pnlAsOfDate(selectedDate) && (
                <> → PnL as of {pnlAsOfDate(selectedDate)}</>
              )}
            </Typography>
          )}
        </Box>

        {!loadingDates && availableDates.length === 0 && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No archive dates available.
          </Typography>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loadingSummary ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Firmwide metrics */}
            <Section>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {SEGMENT_TITLES.firmwide}
              </Typography>
              <GridContainer columns={{ xs: 1, md: 3 }} spacing={3}>
                <MetricCard
                  title="Total P&L"
                  value={firmwide.totalPnl ?? 0}
                  subtitle={formatBasisPoints(
                    calculateBasisPoints(firmwide.totalPnl ?? 0, nav),
                  )}
                  type="currency"
                  color={(firmwide.totalPnl ?? 0) < 0 ? "error" : "success"}
                />
                <MetricCard
                  title="Long Market Value"
                  value={firmwide.totalLmv ?? 0}
                  subtitle={`${formatPercentage(
                    calculateNavPercentage(firmwide.totalLmv ?? 0, nav),
                  )} of NAV`}
                  type="currency"
                  color="info"
                />
                <MetricCard
                  title="Short Market Value"
                  value={firmwide.totalSmv ?? 0}
                  subtitle={`-${formatPercentage(
                    calculateNavPercentage(firmwide.totalSmv ?? 0, nav),
                  )} of NAV`}
                  type="currency"
                  color="error"
                />
              </GridContainer>
            </Section>

            {/* Strategy breakdown */}
            <Section sx={{ mt: 2, borderTop: "solid" }}>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                Strategy Performance
              </Typography>
              <GridContainer columns={{ xs: 1, sm: 2, lg: 3 }} spacing={4}>
                <SummaryBox title={SEGMENT_TITLES.mab} data={summary.mab} />
                <SummaryBox
                  title={SEGMENT_TITLES.credit}
                  data={summary.credit}
                />
                <SummaryBox title={SEGMENT_TITLES.spac} data={summary.spac} />
                <SummaryBox title={SEGMENT_TITLES.event} data={summary.event} />
                <SummaryBox
                  title={SEGMENT_TITLES.creditArb}
                  data={summary.creditArb}
                />
                <SummaryBox title={SEGMENT_TITLES.rest} data={summary.rest} />
              </GridContainer>
            </Section>
          </>
        )}
      </Section>
    </Box>
  );
};

export default FlashPnl;
